/* Suggested path: frontend/src/pages/Skills.jsx
   ====================================================================
   RECONCILIATION NOTES

   Supersedes the previous pass's Skills.jsx (which imported the now-
   retired components/skill-cards/*.jsx). Live skill cards now resolve
   through the same registry Dashboard uses (data/configs/index.js),
   fed by the same useSkills() Supabase fetch — one query, one card
   implementation per shape, shared by both pages.

   Live cards get no onClick/onAction here, so each config's built-in
   fallback renders a real <Link to={skill.route}> for the CTA — no
   extra wiring needed on this page at all for the live cards.

   UPDATED — "Vote for what's next" (heading + Technical Translation's
   card + the early-stage idea poll) is now the shared
   VoteForNextSection component (components/VoteForNextSection.jsx),
   also rendered on Dashboard.jsx. BookSkillCard (Precision
   Storytelling's coming-soon card) is deleted outright — Precision
   Storytelling shipped as a live skill, so it no longer belongs in
   this section at all.
*/
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { usePageTitle } from '@hooks/usePageTitle';
import { useProgress } from '@hooks/useProgress';
import { useSkills } from '@hooks/useSkills';
import ContourBG from '@components/ContourBG';
import SkillLoadingScreen from '@components/SkillLoadingScreen';
import { getSkillCard } from '@data/configs/index.js';
import VoteForNextSection, { LEADERBOARD_ANCHOR } from '@components/VoteForNextSection';
import MasonryGrid from '@components/MasonryGrid';

/* ─── Admin ─── */
const ADMIN_EMAIL = 'plethora1000@gmail.com';
const isAdmin = (email) => email?.toLowerCase() === ADMIN_EMAIL;

/* Unfinished skills hidden from non-admins in the live grid. AI Prompting is the
   only fully public skill. precision-storytelling shipped as status:'live', so
   this entry now actually does something (admin-only gate on a live skill)
   rather than being a no-op for a coming-soon row. */
const ADMIN_ONLY_SKILL_IDS = new Set(['copywriting', 'sales-negotiation', 'precision-storytelling']);

/* ─── Skeleton ─── */
/* ═══════════════════════════════════════════════════════════
   MAIN SKILLS PAGE
═══════════════════════════════════════════════════════════ */
const Skills = () => {
  usePageTitle('Skills — Castle Atlas');
  const { completedExercises = [], loading: progressLoading } = useProgress();
  const { user } = useAuth();
  const isAdminUser = isAdmin(user?.email);
  const { skills, loading: skillsLoading, error: skillsError } = useSkills();
  const location = useLocation();

  const [isDark, setIsDark] = useState(() =>
    typeof window !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const obs = new MutationObserver(() => setIsDark(document.documentElement.classList.contains('dark')));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  // Dashboard's "8 ideas up for vote →" link lands here via a hash —
  // react-router doesn't scroll to it on its own (that's native full-page-
  // navigation behavior, not client-side routing), so do it ourselves once
  // the section has actually rendered.
  useEffect(() => {
    if (progressLoading || skillsLoading) return;
    if (location.hash !== `#${LEADERBOARD_ANCHOR}`) return;
    const id = requestAnimationFrame(() => {
      document.getElementById(LEADERBOARD_ANCHOR)?.scrollIntoView({
        behavior: (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) ? 'auto' : 'smooth',
        block: 'start',
      });
    });
    return () => cancelAnimationFrame(id);
  }, [location.hash, progressLoading, skillsLoading]);

  if (progressLoading || skillsLoading) {
    return <SkillLoadingScreen iconType="logo" iconAlt="Castle Atlas" loadingText="skills" />;
  }

  const getProgress = (skillId) => completedExercises.filter((ex) => ex.skill_id === skillId).length;

  const headColor = isDark ? '#fafaf9' : '#1c1917';
  const mutedColor = isDark ? '#78716c' : '#a8a29e';
  const dashColor = isDark ? '#57534e' : '#d4cdc7';
  const pageBg = isDark ? '#0e0d0c' : '#faf9f7';

  const liveSkills = skills.filter((s) => s.status === 'live' && (!ADMIN_ONLY_SKILL_IDS.has(s.id) || isAdminUser));
  const comingSoon = skills.filter((s) => s.status === 'coming-soon');

  // Resolved up front (not inline in the MasonryGrid render prop) so its
  // index (0..N-1) is stable and matches what MasonryGrid measures/
  // positions — a skill with no registry card just never gets a slot.
  const skillCards = liveSkills
    .map((skill) => ({ skill, Card: getSkillCard(skill.card_shape) }))
    .filter(({ Card }) => Card);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: pageBg }}>
      <style>{`
        @keyframes fade-up { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulse-dot { 0%,100%{box-shadow:0 0 0 0 rgba(34,197,94,0.4)} 50%{box-shadow:0 0 0 4px rgba(34,197,94,0)} }
        .skill-enter { animation: fade-up 0.5s cubic-bezier(0.22,1,0.36,1) both; }

        @media(prefers-reduced-motion:reduce){
          .skill-enter { animation:none!important; }
          [style*="pulse-dot"] { animation:none!important; }
        }
      `}</style>

      <div className="rail-clearance" style={{ maxWidth: '100%', padding: '28px 24px 72px' }}>

        {skillsError && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '10px 16px', marginBottom: 16, color: '#dc2626', fontSize: 13, fontWeight: 600 }}>
            ⚠️ {skillsError}
          </div>
        )}

        {/* ── Header — Atlas Plates shell ── */}
        <div className="skill-enter" style={{ position: 'relative', marginBottom: 32, paddingTop: 8 }}>
          <div style={{ position: 'absolute', top: -8, left: -24, right: -24, height: 140, overflow: 'hidden', pointerEvents: 'none' }}>
            <ContourBG isDark={isDark} size="compact" opacity={0.9} />
          </div>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: '#ea580c', letterSpacing: '0.14em' }}>THE MAP</span>
              <span style={{ width: 22, height: 1, background: dashColor, display: 'inline-block' }} />
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: mutedColor, letterSpacing: '0.14em', textTransform: 'uppercase' }}>Skill Library</span>
            </div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 14px', borderRadius: 99, marginBottom: 12, background: 'rgba(234,88,12,0.06)', border: '1px solid rgba(234,88,12,0.15)' }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', animation: 'pulse-dot 2s ease-in-out infinite' }} />
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: '#ea580c' }}>
                {liveSkills.length} skills live · {comingSoon.length} coming soon
              </span>
            </div>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.6rem,4.5vw,2rem)', fontWeight: 900, letterSpacing: '-0.03em', color: headColor, margin: '0 0 6px', lineHeight: 1.1 }}>Your Skill Library</h1>
            <p style={{ fontSize: 14, color: mutedColor, maxWidth: 480, lineHeight: 1.6, margin: 0 }}>
              Five communication modes. One outcome: you become undeniable.
            </p>
          </div>
        </div>

        {/* Live skills — masonry (components/MasonryGrid.jsx). Cards stay
            flat siblings in skillCards' order (index i), so tab order is
            just that document order regardless of the visual column each
            one lands in. */}
        <MasonryGrid itemCount={skillCards.length}>
          {(i, style, ref) => {
            const { skill, Card } = skillCards[i];
            return (
              <div key={skill.id} ref={ref} className="skill-enter" style={{ ...style, animationDelay: `${80 + i * 80}ms` }}>
                <Card skill={skill} progress={getProgress(skill.id)} isDark={isDark} mode="skills" />
              </div>
            );
          }}
        </MasonryGrid>

        <div className="skill-enter" style={{ marginTop: 48, animationDelay: '400ms' }}>
          <VoteForNextSection
            skills={skills}
            isDark={isDark}
            headColor={headColor}
            mutedColor={mutedColor}
            dashColor={dashColor}
            source="skills_page_vote"
          />
        </div>

        <div className="skill-enter" style={{ marginTop: 48, textAlign: 'center', animationDelay: '640ms' }}>
          <p style={{ fontSize: 13, color: mutedColor }}>More skills dropping in 2026. Vote above to influence what gets built next.</p>
        </div>
      </div>
    </div>
  );
};

export default Skills;
